"use client";

import { Popover, PopoverTrigger, PopoverContent, PopoverClose } from "@/components/ui/popover";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { CalendarCheck, ChevronLeft, ChevronRight, ExternalLink, RefreshCw, Trash2, X } from "lucide-react";
import type { Property, SeenReservation } from "@/lib/types";
import { getPropertyIcon } from "@/lib/property-icons";
import { cn } from "@/lib/cn";
import { useAppData } from "@/lib/data-context";
import { AddReservationModal } from "@/components/add-reservation-modal";
import { ConfirmModal } from "@/components/ui/confirm-modal";
import { createClient } from "@/utils/supabase/client";
import { APP_TIMEZONE, parseAppDate } from "@/lib/app-date";

// Synchronously seed from getSession (local-storage only — no network)
// so the popover doesn't have to wait on the supabase auth proxy round-trip.
let cachedAuthed: boolean | null = null;

function useIsAuthenticated(): boolean {
  const [authed, setAuthed] = useState<boolean>(cachedAuthed ?? false);
  useEffect(() => {
    const supabase = createClient();
    let mounted = true;
    // getSession reads from local storage — instantaneous, no network round-trip.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      cachedAuthed = !!session?.user;
      setAuthed(cachedAuthed);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      if (!mounted) return;
      cachedAuthed = !!session?.user;
      setAuthed(cachedAuthed);
    });
    return () => { mounted = false; subscription.unsubscribe(); };
  }, []);
  return authed;
}

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_LABELS_SHORT = ["S", "M", "T", "W", "T", "F", "S"];

const LANE_HEIGHT = 26;
const BAR_GAP = 3;
const DAY_HEADER_HEIGHT = 30;
const WEEK_PADDING_TOP = 4;
const SLIDE_MS = 220;

// URL filter token derived from a property name. Stable for unchanged names,
// human-readable in shared links (e.g. ?properties=dania-1,cutler-bay).
function slugify(s: string): string {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function parsePropertyParam(raw: string | null): Set<string> {
  if (!raw) return new Set();
  return new Set(raw.split(",").map((s) => s.trim()).filter(Boolean));
}

function toLocalDate(s: string): Date {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function dateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

function nightsBetween(checkin: string, checkout: string): number {
  return Math.round((toLocalDate(checkout).getTime() - toLocalDate(checkin).getTime()) / 86400000);
}

function formatDate(s: string): string {
  // Lock to America/New_York so the displayed weekday/month/day matches the
  // calendar grid (which is built from bare YYYY-MM-DD strings, i.e. NY days).
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: APP_TIMEZONE,
  }).format(parseAppDate(s));
}

function monthCacheKey(y: number, m: number) {
  return `${y}-${m}`;
}

function buildCalendarWeeks(year: number, month: number): Date[][] {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const start = new Date(first);
  start.setDate(start.getDate() - start.getDay());
  const end = new Date(last);
  end.setDate(end.getDate() + (6 - end.getDay()));
  const weeks: Date[][] = [];
  let current = new Date(start);
  while (current <= end) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i++) { week.push(new Date(current)); current = addDays(current, 1); }
    weeks.push(week);
  }
  return weeks;
}

interface EventBar {
  reservation: SeenReservation;
  property: Property;
  startCol: number;
  endCol: number;
  lane: number;
  isCheckinWeek: boolean;
  isCheckoutWeek: boolean;
  leftFrac: number;
  rightFrac: number;
}

function buildEventBars(
  week: Date[],
  reservations: SeenReservation[],
  propertyMap: Map<string, Property>,
  propertyOrderMap: Map<string, number>
): EventBar[] {
  const weekStart = week[0];
  const weekEnd = week[6];
  const bars: EventBar[] = [];
  const seen = new Set<string>();

  for (const res of reservations) {
    if (res.is_cancelled) continue;
    const property = propertyMap.get(res.property_id);
    if (!property) continue;

    const key = `${res.id}-${dateKey(weekStart)}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const checkin = toLocalDate(res.checkin_date);
    const checkout = toLocalDate(res.checkout_date);

    if (checkout < weekStart || checkin > weekEnd) continue;

    const barStart = checkin < weekStart ? weekStart : checkin;
    const barEnd = checkout > weekEnd ? weekEnd : checkout;
    const startCol = barStart.getDay();
    const endCol = barEnd.getDay();
    const isCheckinWeek = checkin >= weekStart && checkin <= weekEnd;
    const isCheckoutWeek = checkout >= weekStart && checkout <= weekEnd;
    const leftFrac = isCheckinWeek ? startCol + 0.5 : startCol;
    const rightFrac = isCheckoutWeek ? endCol + 0.45 : endCol + 1;

    if (rightFrac <= leftFrac) continue;

    // Fixed lane: each property always on its own row by property list index
    const lane = propertyOrderMap.get(res.property_id) ?? 0;

    bars.push({ reservation: res, property, startCol, endCol, lane, isCheckinWeek, isCheckoutWeek, leftFrac, rightFrac });
  }

  return bars;
}

function ReservationCard({ bar }: { bar: EventBar }) {
  const { reservation: res, property } = bar;
  const nights = nightsBetween(res.checkin_date, res.checkout_date);
  const Icon = getPropertyIcon(property.icon ?? "home");
  const { refresh } = useAppData();
  const isAuthed = useIsAuthenticated();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  async function handleDelete() {
    setDeleting(true);
    try {
      await fetch(`/api/reservations/${res.id}`, { method: "DELETE" });
      await refresh();
      setConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-6 w-6 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: property.color }}>
            {/* eslint-disable-next-line react-hooks/static-components -- getPropertyIcon returns a stable component reference from a static map, not a per-render component */}
            <Icon className="h-3.5 w-3.5 text-white" />
          </div>
          <span className="text-sm font-medium text-foreground truncate">{property.name}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isAuthed && (
            <button
              onClick={() => setConfirmOpen(true)}
              className="text-muted-foreground hover:text-destructive transition-colors p-0.5 rounded"
              title="Delete reservation"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
          <PopoverClose className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="h-3.5 w-3.5" />
          </PopoverClose>
        </div>
      </div>

      <ConfirmModal
        open={confirmOpen}
        onOpenChange={(o) => { if (!o && !deleting) setConfirmOpen(false); }}
        title={`Delete reservation at ${property.name}?`}
        description={`${formatDate(res.checkin_date)} → ${formatDate(res.checkout_date)} · ${nights} night${nights !== 1 ? "s" : ""}. The Airbnb feed may re-add it on the next sync.`}
        confirmLabel="Delete"
        destructive
        loading={deleting}
        onConfirm={handleDelete}
      />

      <div className="space-y-1 text-xs">
        <p><span className="text-muted-foreground">Check-in:</span> <span className="text-foreground">{formatDate(res.checkin_date)}</span> <span className="text-muted-foreground">at {property.checkin_time}</span></p>
        <p><span className="text-muted-foreground">Check-out:</span> <span className="text-foreground">{formatDate(res.checkout_date)}</span> <span className="text-muted-foreground">at {property.checkout_time}</span></p>
        <p className="text-foreground font-medium">{nights} night{nights !== 1 ? "s" : ""}</p>
        {res.guest_phone_last4 && <p className="text-muted-foreground">Guest: <span className="text-foreground">****{res.guest_phone_last4}</span></p>}
      </div>
      {isAuthed && res.reservation_url && (
        <a href={res.reservation_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-primary hover:underline">
          View on Airbnb
          <ExternalLink className="h-3 w-3" />
        </a>
      )}
    </div>
  );
}

interface WeeksSnapshot {
  direction: "prev" | "next";
  weeks: Date[][];
  reservations: SeenReservation[];
  propertyMap: Map<string, Property>;
  propertyOrderMap: Map<string, number>;
  propertiesCount: number;
  laneHeight: number;
  loading: boolean;
  month: number;
}

function WeeksPanel({
  weeks,
  reservations,
  propertyMap,
  propertyOrderMap,
  propertiesCount,
  laneHeight,
  loading,
  month,
  todayKey,
}: {
  weeks: Date[][];
  reservations: SeenReservation[];
  propertyMap: Map<string, Property>;
  propertyOrderMap: Map<string, number>;
  propertiesCount: number;
  laneHeight: number;
  loading: boolean;
  month: number;
  todayKey: string;
}) {
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
        Loading…
      </div>
    );
  }

  const fixedMinHeight = DAY_HEADER_HEIGHT + WEEK_PADDING_TOP + Math.max(propertiesCount, 1) * laneHeight;

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {weeks.map((week, wi) => {
        const bars = buildEventBars(week, reservations, propertyMap, propertyOrderMap);

        return (
          <div
            key={wi}
            className={cn("flex-1 relative grid grid-cols-7", wi < weeks.length - 1 && "border-b border-border")}
            style={{ minHeight: Math.max(fixedMinHeight, 48) }}
          >
            {week.map((day, di) => {
              const key = dateKey(day);
              const isToday = key === todayKey;
              const isCurrentMonth = day.getMonth() === month;
              return (
                <div key={di} className={cn("relative h-full", di < 6 && "border-r border-border")}>
                  <div className={cn(
                    "flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full text-xs sm:text-sm mt-1 mx-auto",
                    isToday ? "bg-primary text-primary-foreground font-semibold"
                      : isCurrentMonth ? "text-foreground"
                      : "text-muted-foreground/40"
                  )}>
                    {day.getDate()}
                  </div>
                </div>
              );
            })}

            {bars.map((bar, bi) => {
              const top = DAY_HEADER_HEIGHT + WEEK_PADDING_TOP + bar.lane * laneHeight;
              const leftPct = (bar.leftFrac / 7) * 100;
              const widthPct = ((bar.rightFrac - bar.leftFrac) / 7) * 100;
              const PropertyIcon = getPropertyIcon(bar.property.icon ?? "home");
              const nights = nightsBetween(bar.reservation.checkin_date, bar.reservation.checkout_date);
              const widthCols = bar.rightFrac - bar.leftFrac;

              // Desktop: always show name
              // Mobile: show name based on stay length and available space
              const showNameMobile = bar.isCheckinWeek
                ? nights > 1
                : nights > 1 && widthCols >= 1.4;

              return (
                <Popover key={bi}>
                  <PopoverTrigger asChild>
                    <button
                      className={cn(
                        "absolute flex items-center justify-start gap-1 px-1 text-xs font-medium text-white overflow-hidden cursor-pointer hover:brightness-110 active:brightness-95 transition-[filter] focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                        bar.isCheckinWeek ? "rounded-l-full" : "rounded-l-none",
                        bar.isCheckoutWeek ? "rounded-r-full" : "rounded-r-none"
                      )}
                      style={{
                        top,
                        left: `calc(${leftPct}% + 2px)`,
                        width: `calc(${widthPct}% - 4px)`,
                        height: laneHeight - BAR_GAP,
                        backgroundColor: bar.property.color,
                      }}
                    >
                      <PropertyIcon
                        className="h-3 w-3 shrink-0 opacity-90"
                        style={bar.startCol === 0 && bar.isCheckoutWeek && !bar.isCheckinWeek
                          ? { transform: 'translateX(-2px)' }
                          : undefined}
                      />
                      {/* Desktop: always show name */}
                      <span className="truncate hidden sm:block text-[10px] sm:text-xs leading-none">
                        {bar.property.name}
                      </span>
                      {/* Mobile: conditional */}
                      {showNameMobile && (
                        <span className="truncate sm:hidden text-[9px] leading-none">
                          {bar.property.name}
                        </span>
                      )}
                      {bar.reservation.platform === "vrbo" && (
                        <span
                          aria-label="VRBO"
                          title="VRBO"
                          className="ml-auto flex h-3 w-3 shrink-0 items-center justify-center rounded-[3px] bg-white/25 text-[8px] font-bold leading-none"
                        >
                          V
                        </span>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    sideOffset={6}
                    collisionPadding={12}
                    className="w-64 p-3"
                  >
                    <ReservationCard bar={bar} />
                  </PopoverContent>
                </Popover>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

function isTouchDevice() {
  return typeof window !== "undefined" && window.matchMedia("(hover: none) and (pointer: coarse)").matches;
}

export function CalendarView() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  // Property filter — selection is kept as a set of name slugs (robust to the
  // async property load and to reordering). Seeded from the URL so shared
  // links open pre-filtered; mirrored back to the URL on change (no reload).
  const searchParams = useSearchParams();
  const [selectedSlugs, setSelectedSlugs] = useState<Set<string>>(
    () => parsePropertyParam(searchParams.get("properties"))
  );

  const { reservations: allReservations, properties, loading, refresh } = useAppData();
  const isAuthed = useIsAuthenticated();
  // Starts true: the sync POST below always fires on mount, so the indicator
  // should show immediately rather than after a synchronous setState in the effect.
  const [syncing, setSyncing] = useState(true);
  const [slideSnapshot, setSlideSnapshot] = useState<WeeksSnapshot | null>(null);
  const isAnimatingRef = useRef(false);
  const slideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const touchStartXRef = useRef(0);
  const weeksAreaRef = useRef<HTMLDivElement>(null);
  const [weeksAreaHeight, setWeeksAreaHeight] = useState(0);

  useEffect(() => {
    const el = weeksAreaRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWeeksAreaHeight(entry.contentRect.height));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const weeks = useMemo(() => buildCalendarWeeks(year, month), [year, month]);

  const reservations = useMemo(() => {
    const from = dateKey(weeks[0][0]);
    const to = dateKey(weeks[weeks.length - 1][6]);
    return allReservations.filter(r => r.checkout_date >= from && r.checkin_date <= to);
  }, [allReservations, weeks]);

  useEffect(() => {
    fetch("/api/sync", { method: "POST" })
      .then(() => refresh())
      .catch(() => {})
      .finally(() => setSyncing(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Properties whose slug is currently selected. A selected slug that matches
  // no live property (e.g. a renamed property in an old link) is ignored, so a
  // stale link never produces an empty calendar — it falls back to "show all".
  const selectedProperties = useMemo(
    () => properties.filter((p) => selectedSlugs.has(slugify(p.name))),
    [properties, selectedSlugs]
  );
  const filterActive = selectedProperties.length > 0;
  const visibleProperties = filterActive ? selectedProperties : properties;

  // Mirror the selection into the URL without a navigation/reload so the
  // filtered view is shareable. Runs on mount too (normalizes the param).
  // Other query params are preserved (and stay percent-encoded); the property
  // slugs are URL-safe so we join them with a literal comma for clean links.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    params.delete("properties");
    const rest = params.toString();
    const props = selectedSlugs.size ? `properties=${[...selectedSlugs].join(",")}` : "";
    const qs = [rest, props].filter(Boolean).join("&");
    const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.history.replaceState(null, "", url);
  }, [selectedSlugs]);

  const toggleProperty = useCallback((slug: string) => {
    setSelectedSlugs((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }, []);

  const clearFilter = useCallback(() => setSelectedSlugs(new Set()), []);

  const propertyMap = useMemo(() => new Map(visibleProperties.map(p => [p.id, p])), [visibleProperties]);
  const propertyOrderMap = useMemo(() => new Map(visibleProperties.map((p, i) => [p.id, i])), [visibleProperties]);

  const effectiveLaneHeight = weeksAreaHeight === 0
    ? Math.min(LANE_HEIGHT, 18)
    : Math.max(
        BAR_GAP + 4,
        Math.min(
          LANE_HEIGHT,
          Math.floor(
            (weeksAreaHeight / weeks.length - DAY_HEADER_HEIGHT - WEEK_PADDING_TOP) /
            Math.max(visibleProperties.length, 1)
          )
        )
      );

  function prevMonth() {
    if (month === 0) { setYear(y => y - 1); setMonth(11); } else setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 11) { setYear(y => y + 1); setMonth(0); } else setMonth(m => m + 1);
  }
  function goToday() { setYear(today.getFullYear()); setMonth(today.getMonth()); }

  function animateAndNavigate(direction: "prev" | "next") {
    if (isAnimatingRef.current) return;
    isAnimatingRef.current = true;
    if (slideTimerRef.current) clearTimeout(slideTimerRef.current);

    setSlideSnapshot({
      direction,
      weeks,
      reservations,
      propertyMap,
      propertyOrderMap,
      propertiesCount: visibleProperties.length,
      laneHeight: effectiveLaneHeight,
      loading,
      month,
    });

    direction === "prev" ? prevMonth() : nextMonth();

    slideTimerRef.current = setTimeout(() => {
      setSlideSnapshot(null);
      isAnimatingRef.current = false;
    }, SLIDE_MS + 30);
  }

  function handleNavClick(direction: "prev" | "next") {
    if (isTouchDevice()) {
      animateAndNavigate(direction);
    } else {
      direction === "prev" ? prevMonth() : nextMonth();
    }
  }

  function handleTouchStart(e: React.TouchEvent) {
    touchStartXRef.current = e.touches[0].clientX;
  }
  function handleTouchEnd(e: React.TouchEvent) {
    const dx = e.changedTouches[0].clientX - touchStartXRef.current;
    if (Math.abs(dx) > 50) animateAndNavigate(dx > 0 ? "prev" : "next");
  }

  const monthLabel = new Date(year, month, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const todayKey = dateKey(today);

  const legendItems = properties.map(p => {
    const Icon = getPropertyIcon(p.icon ?? "home");
    const slug = slugify(p.name);
    const isSelected = selectedSlugs.has(slug);
    const dimmed = filterActive && !isSelected;
    return (
      <button
        key={p.id}
        type="button"
        onClick={() => toggleProperty(slug)}
        aria-pressed={isSelected}
        title={
          isSelected
            ? `Remove ${p.name} from filter`
            : filterActive
              ? `Add ${p.name} to filter`
              : `Filter calendar to ${p.name}`
        }
        className={cn(
          "flex items-center gap-1.5 min-w-0 rounded-full px-2 py-1 transition-colors",
          "hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
          isSelected && "bg-accent",
          dimmed && "opacity-40 hover:opacity-100"
        )}
      >
        <div className="h-5 w-5 rounded-full flex items-center justify-center shrink-0" style={{ backgroundColor: p.color }}>
          <Icon className="h-3 w-3 text-white" />
        </div>
        <span className={cn("text-xs truncate", isSelected ? "text-foreground font-medium" : "text-muted-foreground")}>{p.name}</span>
      </button>
    );
  });

  const clearFilterButton = filterActive ? (
    <button
      type="button"
      onClick={clearFilter}
      className="shrink-0 rounded-full px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-accent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
    >
      Show all
    </button>
  ) : null;

  const monthNav = (
    <div className="flex items-center gap-1">
      <button
        onClick={() => handleNavClick("prev")}
        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <h2 className="relative text-base sm:text-xl font-semibold text-foreground w-[155px] sm:w-[210px] text-center">
        {monthLabel}
        <RefreshCw className={cn("absolute right-0 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground", syncing ? "animate-spin" : "invisible")} />
      </h2>
      <button
        onClick={() => handleNavClick("next")}
        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
      >
        <ChevronRight className="h-5 w-5" />
      </button>
    </div>
  );

  const slideOutAnim = slideSnapshot
    ? `cal-slide-out-${slideSnapshot.direction === "prev" ? "right" : "left"} ${SLIDE_MS}ms ease-in-out forwards`
    : undefined;
  const slideInAnim = slideSnapshot
    ? `cal-slide-in-${slideSnapshot.direction === "prev" ? "left" : "right"} ${SLIDE_MS}ms ease-in-out forwards`
    : undefined;

  const weeksPanelProps = {
    propertyMap,
    propertyOrderMap,
    propertiesCount: visibleProperties.length,
    laneHeight: effectiveLaneHeight,
    todayKey,
  };

  return (
    // Fill-height calendar. On mobile the shell scrolls at the document level,
    // so pin our own viewport height (minus the h-12 sticky header) instead of
    // relying on a fixed-height parent. Desktop keeps h-full inside the panel.
    <div className="flex flex-col h-[calc(100dvh-3rem-env(safe-area-inset-top))] md:h-full p-4 sm:p-6">
      {/* Desktop header */}
      <div className="shrink-0 pb-1.5">
        <div className="hidden md:grid items-center gap-2" style={{ gridTemplateColumns: "1fr auto 1fr" }}>
          <div className="flex flex-wrap gap-x-1 gap-y-1 items-center">
            {legendItems}
            {clearFilterButton}
          </div>
          {monthNav}
          <div className="flex justify-end items-center gap-2">
            <button onClick={goToday} className="text-sm text-muted-foreground hover:text-foreground px-3 py-1.5 rounded-md hover:bg-accent transition-colors whitespace-nowrap">
              Today
            </button>
            {isAuthed && <AddReservationModal properties={properties.map((p) => ({ id: p.id, name: p.name }))} />}
          </div>
        </div>

        {/* Mobile header */}
        <div className="md:hidden space-y-1">
          <div className="flex items-center justify-center">
            {monthNav}
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-wrap gap-x-1 gap-y-1 items-center min-w-0">
              {legendItems}
              {clearFilterButton}
            </div>
            <button
              onClick={goToday}
              className="shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              aria-label="Go to today"
              title="Today"
            >
              <CalendarCheck className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Calendar grid */}
      <div className="flex-1 min-h-0 flex flex-col rounded-lg border border-border overflow-hidden">
        <div className="shrink-0 grid grid-cols-7 border-b border-border">
          {DAY_LABELS.map((d, i) => (
            <div key={d} className="py-2 text-center text-xs font-medium text-muted-foreground">
              <span className="hidden sm:inline">{d}</span>
              <span className="sm:hidden">{DAY_LABELS_SHORT[i]}</span>
            </div>
          ))}
        </div>

        <div
          ref={weeksAreaRef}
          className="flex-1 min-h-0 relative overflow-hidden"
          style={{ touchAction: "pan-x" }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {slideSnapshot && (
            <div className="absolute inset-0 flex flex-col" style={{ animation: slideOutAnim }}>
              <WeeksPanel
                weeks={slideSnapshot.weeks}
                reservations={slideSnapshot.reservations}
                propertyMap={slideSnapshot.propertyMap}
                propertyOrderMap={slideSnapshot.propertyOrderMap}
                propertiesCount={slideSnapshot.propertiesCount}
                laneHeight={slideSnapshot.laneHeight}
                loading={slideSnapshot.loading}
                month={slideSnapshot.month}
                todayKey={todayKey}
              />
            </div>
          )}

          <div className="absolute inset-0 flex flex-col" style={{ animation: slideInAnim }}>
            <WeeksPanel
              weeks={weeks}
              reservations={reservations}
              loading={loading}
              month={month}
              {...weeksPanelProps}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
